import {setSelectValue, spendCapValidator} from 'js/reusable/formHelper';
import {getSpendCapTransactionModes} from 'js/admin/helpers/overlay/overlay';
import {__, options} from 'js/tool/page/current';
import {formatNumber} from 'js/admin/helpers/grid';
import {parseNumber} from 'js/tool/number';
import {getCurrencyMetaData} from 'js/tool/metaData/helper';
import {cloneDeep} from 'lodash';
import {SpendCapAction} from 'graphql/generated-fbgraph/graphql';

export const UNLIMITED_SPEND_CAP_VALUE = 0;

export function getSpendCapFormValues(externalAdAccount, refRequired = true) {
	return {
		externalAdAcc: {valueSetter: setSelectValue, value: externalAdAccount},
		spendCap:      {
			value:     '',
			validator: spendCapValidator,
			required:  false,
			currency:  externalAdAccount?.currency ?? null,
		},
		transactionMode: {
			valueSetter: setSelectValue,
			value:       getSpendCapTransactionModes({
				isUnlimited:      isSpendCapUnlimited(externalAdAccount.spendCap),
				fundsLeft:        externalAdAccount.fundsLeft,
				includeSetAction: false,
			})[0],
		},
		note:   {value: '', required: false},
		refNum: {value: '', required: refRequired},
	};
}

export function getNotificationTexts(values, externalAccountsInfo, newFundsLeft) {
	const value = formatNumber(
		parseFloat(values.spendCap),
		getLocaleOptions(externalAccountsInfo.currencyTag),
		'currency'
	);

	const transactionModeValue = values.transactionMode ? values.transactionMode.value : '';
	const transactionModeLabel = values.transactionMode ? values.transactionMode.label : '';
	const externalAdAccLabel   = values.externalAdAcc ? values.externalAdAcc.label : '';

	const {facebookCurrencyMinValue} = getCurrencyMetaData(externalAccountsInfo.currencyTag);

	const valueZero = formatNumber(
		facebookCurrencyMinValue, getLocaleOptions(externalAccountsInfo.currencyTag), 'currency'
	);

	return {
		actionLabel: transactionModeValue === SpendCapAction.REMOVE_ALL ? __('fb:remove') : transactionModeLabel,
		actionValue: transactionModeValue === SpendCapAction.REMOVE_ALL ? __('fb:all funds') : value,
		accVerb:
			transactionModeValue === SpendCapAction.ADD || transactionModeValue === SpendCapAction.SET
				? __('fb:to')
				: __('fb:from'),
		newValue:     transactionModeValue === SpendCapAction.REMOVE_ALL ? valueZero : newFundsLeft,
		extAdAccName: externalAdAccLabel,
	};
}

export function getFundsLeft(values) {
	if (!values.externalAdAcc) {
		return '-';
	}

	const localeOptions = getLocaleOptions();
	localeOptions.currencyTag = values.externalAdAcc ? values.externalAdAcc.currency : '';

	return formatNumber(
		Number(values.externalAdAcc.fundsLeft) - getCurrencyMetaData(
			values.externalAdAcc.currency
		).facebookCurrencyMinValue,
		localeOptions,
		'currency'
	);
}

export function getNewFundsLeft({values, externalAccountsInfo, asNumber}) {
	let newFundsLeft;

	if (values.transactionMode.value === SpendCapAction.ADD && values.externalAdAcc) {
		newFundsLeft = values.externalAdAcc.fundsLeft + parseNumber(values.spendCap);
	}

	if (values.transactionMode.value === SpendCapAction.REMOVE && values.externalAdAcc) {
		newFundsLeft = values.externalAdAcc.fundsLeft - parseNumber(values.spendCap);
	}

	if (values.transactionMode.value === SpendCapAction.REMOVE_ALL && values.externalAdAcc) {
		newFundsLeft = values.externalAdAcc.fundsLeft + parseNumber(values.spendCap);
	}

	if (values.transactionMode.value === SpendCapAction.SET && values.externalAdAcc) {
		newFundsLeft = parseNumber(values.spendCap);
	}

	const localeOptions = getLocaleOptions();

	localeOptions.currencyTag = externalAccountsInfo.currencyTag;

	if (asNumber) {
		return newFundsLeft;
	}

	return formatNumber(
		newFundsLeft,
		localeOptions,
		'currency'
	);
}

export function getLocaleOptions(currencyTag, decimalPlaces) {
	return {
		decimalPlaces: decimalPlaces ?
			(options?.locale?.frac_digits ?? undefined)
			: getCurrencyMetaData(currencyTag).decimalPlaces,
		decimalPoint: options?.locale?.decimal_point ?? '.',
		thousandsSep: options?.locale?.thousands_sep ?? ',',
		currencyTag,
	};
}

export function mapValidationErrors(values, validationErrors) {
	if (
		values.transactionMode &&
		values.transactionMode.value === SpendCapAction.REMOVE &&
		values.externalAdAcc &&
		Number(values.spendCap) + getCurrencyMetaData(
			values.externalAdAcc.currency
		).facebookCurrencyMinValue > values.externalAdAcc.fundsLeft
	) {
		validationErrors.spendCap =
			__('fb:removed too much', {MAX: getFundsLeft(values)}
			);
	}

	return validationErrors;
}

export function getUpdateSpendCapMutationVariables(externalAccountsInfo, values) {
	return {
		externalAdAccountId: externalAccountsInfo.externalAdAccountId,
		action:              values.transactionMode.value,
		value: 
			values.transactionMode.value === SpendCapAction.REMOVE_ALL 
				? null 
				: parseNumber(values.spendCap),
	
	};
}

export function calculateFundsLeft(data) {
	const MIN_FUNDS_LEFT_VALUE = getCurrencyMetaData(data?.data?.refreshExternalAdAccounts?.items?.[0].currency);
	const fundsLeft = Number(data?.data?.refreshExternalAdAccounts?.items?.[0].spendCap) -
		Number(data?.data?.refreshExternalAdAccounts?.items?.[0].amountSpent) -
		MIN_FUNDS_LEFT_VALUE;

	return fundsLeft >= MIN_FUNDS_LEFT_VALUE ? fundsLeft : 0;
}

export function getReducedValues(accountData, displayAsFundsLeft = false) {
	const sumValues = [...accountData].reduce((accu, fbAcc) => {
		if (accu.currency === null && accu.nAccWithMCL === null) {
			accu.currency = fbAcc.currency;
			accu.nAccWithMCL = (fbAcc.adAccountSpendCapLimit) ? 1 : 0;

			return accu;
		}

		if (accu.currency !== fbAcc.currency) {
			accu.currency = 'mixed';
		}

		if (fbAcc.adAccountSpendCapLimit) {
			accu.nAccWithMCL += 1;
		}

		return accu;
	}, {
		currency:    null,
		nAccWithMCL: null,
	});

	if (sumValues.currency === 'mixed') {
		return {
			value:       '-',
			nAccWithMCL: sumValues.nAccWithMCL,
		};
	}

	const accountSpendCapValues = [...accountData].reduce(
		(accu, fbAcc) => accu + (
			displayAsFundsLeft ? Number(fbAcc.currSpendCap) - Number(fbAcc.amountSpent) : Number(fbAcc.currSpendCap)
		), 0);

	return {
		value: formatNumber(
			accountSpendCapValues,
			getLocaleOptions(sumValues.currency),
			'currency'
		),
		nAccWithMCL: sumValues.nAccWithMCL,
	};
}

export function getPossibleSpendCapActionModes(accounts) {
	const clonedAccounts = cloneDeep(accounts);
	const possibleModes = clonedAccounts.map(acc => getSpendCapTransactionModes({
		isUnlimited:  isSpendCapUnlimited(acc.spendCap),
		fundsLeft:    acc.fundsLeft,
		minFundsLeft: getCurrencyMetaData(acc.currency).facebookCurrencyMinValue,
	}));

	const firstArray = possibleModes.shift();

	const intersection = firstArray.filter(firstArrayObj =>
		possibleModes.every(array =>
			array.some(arrayObj =>
				firstArrayObj.value === arrayObj.value && firstArrayObj.label === arrayObj.label
			)
		)
	);

	return intersection;
}

/**
 * Can either pass in an object which has spendCap property
 * or number
 *
 * @param {number | {...any, spendCap: number} | null | undefined} item
 * @returns boolean
 */
export function isSpendCapUnlimited(item) {
	return (item?.spendCap ?? item) === UNLIMITED_SPEND_CAP_VALUE;
}

export function preprocessEditableAdAccounts(adAccounts) {
	const possibleModes = getPossibleSpendCapActionModes(adAccounts);

	return adAccounts.map(acc => ({
		...acc,
		mode:             acc?.mode ?? possibleModes[0],
		editableSpendCap: acc?.editableSpendCap ?? '',
		currSpendCap:     acc.spendCap,
	}));
}
